<template>
  <picture class="responsive-image">
    <source
      v-for="source in sources"
      :key="source.media"
      :media="source.media"
      type="image/jpeg"
      :srcset="source.srcset.join(', ')"
    />
    <img
      v-if="fallback"
      width="380"
      height="285"
      :alt="alt"
      :loading="noLazyLoading ? 'eager' : 'lazy'"
      :src="fallback"
      :class="imageClass"
      :draggable="noDraggable ? 'false' : undefined"
    />
  </picture>
</template>

<script lang="ts" setup>
/* Old code:
export default Vue.extend({
 name: 'ResponsiveImage',
 components: {},
 props: {
   url: {
     type: String,
     default: '',
   } as PropOptions<string>,
   alt: {
     type: String,
     default: '',
   } as PropOptions<string>,
   title: {
     type: String,
     default: '',
   } as PropOptions<string>,
   hash: {
     type: String,
     default: '',
   } as PropOptions<string>,
   responsiveStyle: {
     type: String,
     default: '',
   } as PropOptions<string>,
   noLazyLoading: {
     type: Boolean,
     default: false,
   } as PropOptions<boolean>,
 },
*/
import type { ResponsiveStyleName } from '~/helpers/responsiveStyles'
import {
  responsiveStylesMap,
  BREAKPOINTS,
  breakpointOrder,
  getImageStyleUrl,
  getBaseUrl,
} from '~/helpers/responsiveStyles'
import type { ClassProp } from '~/types/vue'

interface Source {
  media: string
  srcset: string[]
  breakpoint: string
}

const props = withDefaults(
  defineProps<{
    url: string
    alt?: string
    title?: string
    hash?: string
    responsiveStyle?: ResponsiveStyleName
    noLazyLoading?: boolean
    imageClass?: ClassProp
    noDraggable?: boolean
  }>(),
  {
    url: '',
    alt: '',
    title: '',
    hash: '',
    responsiveStyle: 'column_full',
    noLazyLoading: false,
    imageClass: '',
    noDraggable: false,
  },
)

/* Old code:
computed: {
 baseUrl(): string {
   return getBaseUrl(this.url as string) || ''
 },
 responsiveStyleItem(): ResponsiveStyle {
   return (responsiveStylesMap.get(this.responsiveStyle) ||
     responsiveStylesMap.get(DEFAULT_STYLE)) as ResponsiveStyle
 },
 fallback(): string | undefined {
   if (!this.baseUrl) {
     return
   }
   const style =
     this.responsiveStyleItem?.styles?.[0].id || '300_225_focal_scale_crop'
   return getImageStyleUrl(style, this.baseUrl, this.hash)
 },
 sources() {
   if (!this.baseUrl) {
     return []
   }
   const sources: Record<string, Source> = {}
   this.responsiveStyleItem?.styles.forEach((style) => {
     if (!sources[style.viewport]) {
       sources[style.viewport] = {
         media: BREAKPOINTS[style.viewport],
         srcset: [],
         breakpoint: style.viewport,
       }
     }
     sources[style.viewport].srcset.push(
       `${getImageStyleUrl(style.id, this.baseUrl, this.hash)} ${
         style.density
       }`
     )
   })
   return Object.keys(sources)
     .map((v) => sources[v])
     .sort((a, b) => {
       return (
         breakpointOrder.indexOf(a.breakpoint) -
         breakpointOrder.indexOf(b.breakpoint)
       )
     })
 },
*/

const baseUrl = computed(() => getBaseUrl(props.url) || '')

const responsiveStyleItem = computed(() =>
  responsiveStylesMap.get(props.responsiveStyle),
)

const fallback = computed(() => {
  if (!baseUrl.value) {
    return undefined
  }
  const style =
    responsiveStyleItem.value?.styles?.[0].id || '300_225_focal_scale_crop'
  return getImageStyleUrl(style, baseUrl.value, props.hash)
})

const sources = computed(() => {
  if (!baseUrl.value) {
    return []
  }
  const sources: Record<string, Source> = {}
  responsiveStyleItem.value?.styles.forEach((style) => {
    if (!sources[style.viewport]) {
      sources[style.viewport] = {
        media: BREAKPOINTS[style.viewport],
        srcset: [],
        breakpoint: style.viewport,
      }
    }
    sources[style.viewport].srcset.push(
      `${getImageStyleUrl(style.id, baseUrl.value, props.hash)} ${
        style.density
      }`,
    )
  })
  return Object.keys(sources)
    .map((v) => sources[v])
    .sort((a, b) => {
      return (
        breakpointOrder.indexOf(a.breakpoint) -
        breakpointOrder.indexOf(b.breakpoint)
      )
    })
})
</script>

<style lang="postcss">
.responsive-image {
  @apply block bg-scheme-main-bis;
  img {
    @apply block w-full;
  }
}
</style>
